<template>
  <div style="max-width: 90%; margin: auto" class="py-10">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-8 flex-wrap" style="gap: 16px">
      <div>
        <p class="page-subtitle mb-1">Section</p>
        <h1 class="page-title">Neue Section anlegen</h1>
      </div>
      <button class="secondary-btn" @click="$router.push('/sections')">
        <v-icon size="16">mdi-arrow-left</v-icon>
        <span>Abbrechen</span>
      </button>
    </div>

    <div class="grid-split">
      <!-- Form -->
      <div class="card-modern">
        <h2 class="section-title mb-5">Grundeinstellungen</h2>

        <div class="fg mb-4">
          <label class="fl">Titel *</label>
          <div class="iw" :class="{ 'iw-f': focusField === 'title' }">
            <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-format-title</v-icon>
            <input
              v-model="section.title"
              class="ii"
              placeholder="z.B. Hero Section"
              @focus="focusField = 'title'"
              @blur="focusField = null"
            />
          </div>
        </div>

        <div class="fg mb-4">
          <label class="fl">Handle <span class="fl-hint">(automatisch generiert)</span></label>
          <div class="iw iw-readonly">
            <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-link-variant</v-icon>
            <input v-model="section.handle" class="ii" readonly />
          </div>
        </div>

        <div class="fg mb-4">
          <label class="fl">Ist die Section kostenlos? *</label>
          <div
            class="radio-option"
            :class="{ 'radio-active': section.isFree === 1 }"
            @click="section.isFree = 1"
          >
            <div class="radio-dot" :class="{ 'radio-dot-on': section.isFree === 1 }"></div>
            <div class="radio-icon" style="background: rgba(46,125,50,0.1)">
              <v-icon size="14" color="#2E7D32">mdi-gift-outline</v-icon>
            </div>
            <div class="radio-body">
              <div class="radio-label">Ja, kostenlos</div>
              <div class="radio-desc">Section wird als Free angeboten</div>
            </div>
          </div>
          <div
            class="radio-option mt-2"
            :class="{ 'radio-active': section.isFree === 0 }"
            @click="section.isFree = 0"
          >
            <div class="radio-dot" :class="{ 'radio-dot-on': section.isFree === 0 }"></div>
            <div class="radio-icon" style="background: rgba(255,152,0,0.1)">
              <v-icon size="14" color="#E65100">mdi-star</v-icon>
            </div>
            <div class="radio-body">
              <div class="radio-label">Nein, kostenpflichtig</div>
              <div class="radio-desc">Premium-Section mit festgelegtem Preis</div>
            </div>
          </div>
        </div>

        <div v-if="section.isFree === 0" class="fg mb-4">
          <label class="fl">Preis *</label>
          <div class="iw" :class="{ 'iw-f': focusField === 'price' }">
            <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-currency-eur</v-icon>
            <input
              :value="priceInput"
              class="ii"
              placeholder="z.B. 19,99"
              @input="onPriceInput($event.target.value)"
              @focus="onPriceFocus(); focusField = 'price'"
              @blur="onPriceBlur(); focusField = null"
            />
          </div>
        </div>

        <div class="fg mb-4">
          <label class="fl">Demo Store Link</label>
          <div class="iw" :class="{ 'iw-f': focusField === 'demo' }">
            <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-open-in-new</v-icon>
            <input
              v-model="section.demoStore"
              class="ii"
              placeholder="https://…"
              @focus="focusField = 'demo'"
              @blur="focusField = null"
            />
          </div>
        </div>

        <!-- Custom Category Dropdown -->
        <div class="fg mb-5" ref="categoryDropdown">
          <label class="fl">Kategorie</label>
          <button
            type="button"
            class="iw iw-trigger"
            :class="{ 'iw-f': categoryMenuOpen }"
            @click="categoryMenuOpen = !categoryMenuOpen"
          >
            <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-tag-outline</v-icon>
            <span class="ii-val">{{ currentCategoryName }}</span>
            <v-icon size="14" color="#888" :class="{ 'chev-open': categoryMenuOpen }">mdi-chevron-down</v-icon>
          </button>
          <div v-if="categoryMenuOpen" class="combo-menu">
            <button type="button" class="combo-option" @click="selectCategory(null)">
              <span class="combo-label">Keine Kategorie</span>
              <v-icon v-if="section.categoryId === null" size="14" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
            </button>
            <div v-if="categories.length" class="combo-divider"></div>
            <button
              v-for="cat in categories"
              :key="cat.id"
              type="button"
              class="combo-option"
              @click="selectCategory(cat.id)"
            >
              <span class="combo-label">{{ cat.name }}</span>
              <v-icon v-if="section.categoryId === cat.id" size="14" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
            </button>
          </div>
        </div>

        <div class="divider-soft mb-5"></div>

        <div class="info-banner">
          <v-icon class="info-banner-icon" size="14" color="#5B5BD6">mdi-information-outline</v-icon>
          <span>Neue Sections werden als <strong>Entwurf</strong> angelegt. Status, Vertriebskanäle und Tag setzt du im Detail-View, sobald die Section fertiggestellt ist.</span>
        </div>

        <div v-if="errorMessage" class="alert-error mt-4">
          <v-icon size="16" color="#D32F2F">mdi-alert-circle-outline</v-icon>
          {{ errorMessage }}
        </div>

        <button
          class="submit-btn submit-btn-dark mt-6"
          :disabled="!section.title || !section.handle || creating"
          @click="createSection"
        >
          <v-progress-circular v-if="creating" indeterminate size="14" width="2" color="white" />
          <v-icon v-else size="16">mdi-file-document-edit-outline</v-icon>
          <span>Section als Entwurf anlegen</span>
        </button>
      </div>

      <!-- Image Side -->
      <div class="card-modern image-side">
        <h2 class="section-title mb-4">
          <div class="title-icon"><v-icon size="16" color="#0a1628">mdi-image-outline</v-icon></div>
          Vorschaubild *
        </h2>

        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          style="display: none"
          @change="onFileSelected"
        />

        <div class="upload-box" @click="triggerFileDialog">
          <v-img v-if="imagePreview" :src="imagePreview" cover aspect-ratio="1" />
          <div v-else class="upload-placeholder">
            <v-icon size="44" color="#c5cbd3" class="mb-3">mdi-camera-outline</v-icon>
            <div class="upload-title">Bild hochladen</div>
            <div class="upload-sub">Klicke zum Auswählen</div>
          </div>
        </div>

        <button v-if="imagePreview" class="bs mt-3" @click="triggerFileDialog">
          <v-icon size="15">mdi-image-edit-outline</v-icon>
          Bild ändern
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SectionCreate',
  data() {
    return {
      categories: [],
      section: {
        title: null,
        handle: '',
        isFree: 1,
        price: null,
        categoryId: null,
        demoStore: null,
        imageFile: null,
      },
      priceInput: '',
      imagePreview: null,
      imageHasTransparency: false,
      usedHandles: new Set(),
      baseNumber: null,
      debounceTimer: null,
      debounceMs: 400,
      creating: false,
      errorMessage: '',
      categoryMenuOpen: false,
      focusField: null,
    };
  },

  computed: {
    currentCategoryName() {
      if (!this.section.categoryId) return 'Kategorie auswählen';
      const cat = this.categories.find((c) => c.id === this.section.categoryId);
      return cat?.name || 'Kategorie auswählen';
    },
  },

  watch: {
    'section.isFree'(v) {
      if (v === 1) {
        this.section.price = null;
        this.priceInput = '';
      }
    },
    'section.title'(val) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        const t = (val || '').trim();
        if (!t) { this.section.handle = ''; return; }
        this.updateHandleFromTitle(t);
      }, this.debounceMs);
    },
  },

  mounted() {
    this.getAllHandles();
    this.getSectionCategories();
    document.addEventListener('click', this.handleOutsideClick);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },

  methods: {
    handleOutsideClick(e) {
      if (this.categoryMenuOpen) {
        const el = this.$refs.categoryDropdown;
        if (el && !el.contains(e.target)) this.categoryMenuOpen = false;
      }
    },
    selectCategory(id) {
      this.section.categoryId = id;
      this.categoryMenuOpen = false;
    },

    slugifyTitle(t) {
      if (!t) return '';
      const map = { ä: 'ae', ö: 'oe', ü: 'ue', Ä: 'ae', Ö: 'oe', Ü: 'ue', ß: 'ss' };
      return t.replace(/[äöüÄÖÜß]/g, (m) => map[m] || m)
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
        .replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    },

    getNextAvailableNumber() {
      let num = (this.baseNumber || 1000) + 1;
      while (this.usedHandles.has(num)) num++;
      return num;
    },

    updateHandleFromTitle(title) {
      const num = this.getNextAvailableNumber();
      const slug = this.slugifyTitle(title);
      this.section.handle = slug ? `${num}_${slug}` : '';
    },

    onPriceInput(val) {
      let cleaned = (val || '').toString().replace(/[^\d.,]/g, '');
      const firstComma = cleaned.indexOf(',');
      const firstDot = cleaned.indexOf('.');
      let firstSep = -1;
      if (firstComma !== -1 && firstDot !== -1) firstSep = Math.min(firstComma, firstDot);
      else firstSep = Math.max(firstComma, firstDot);
      if (firstSep !== -1) {
        const head = cleaned.slice(0, firstSep + 1);
        const tail = cleaned.slice(firstSep + 1).replace(/[.,]/g, '');
        cleaned = head + tail;
      }
      this.priceInput = cleaned;
      const numeric = parseFloat(cleaned.replace(',', '.'));
      this.section.price = isNaN(numeric) ? null : numeric;
    },

    onPriceFocus() {
      if (typeof this.section.price === 'number') {
        const parts = this.section.price.toFixed(2).split('.');
        this.priceInput = parts[0] + ',' + parts[1];
      }
    },

    onPriceBlur() {
      if (typeof this.section.price === 'number') {
        this.priceInput = new Intl.NumberFormat('de-DE', {
          style: 'currency', currency: 'EUR',
          minimumFractionDigits: 2, maximumFractionDigits: 2,
        }).format(this.section.price);
      } else {
        this.priceInput = '';
      }
    },

    async createSection() {
      this.creating = true;
      this.errorMessage = '';
      try {
        const form = new FormData();
        form.append('title', this.section.title ?? '');
        form.append('handle', this.section.handle ?? '');
        form.append('isFree', this.section.isFree ?? 1);
        form.append('categoryId', this.section.categoryId ?? '');
        form.append('demoStore', this.section.demoStore ?? '');
        if (typeof this.section.price === 'number') {
          form.append('price', this.section.price.toFixed(2));
        } else {
          form.append('price', '');
        }
        if (this.section.imageFile) {
          const compressed = await this.compressImage(this.section.imageFile);
          form.append('image', compressed, 'preview.jpg');
        }
        const { data } = await axios.post('/api/sections/create', form, { headers: {} });
        if (data && data.success) {
          this.$router.push({ name: 'SectionOverview' });
        } else {
          throw new Error(data?.message || 'Unerwartete Antwort vom Server');
        }
        return data;
      } catch (e) {
        console.error('Fehler beim Speichern!', e);
        this.errorMessage = e.response?.data?.message || 'Fehler beim Erstellen der Section';
      } finally {
        this.creating = false;
      }
    },

    async getSectionCategories() {
      try {
        const result = await axios.get('/api/category/get');
        this.categories = result.data.map((cat) => ({ ...cat, id: parseInt(cat.id) }));
      } catch (e) { console.error('Fehler beim Laden!', e); }
    },

    async getAllHandles() {
      try {
        const { data } = await axios.get('/api/sections/all-handles');
        const numbers = new Set();
        let maxNumber = 1000;
        data.handles.forEach((handle) => {
          const match = handle.match(/^(\d+)_/);
          if (match) {
            const num = parseInt(match[1], 10);
            numbers.add(num);
            if (num > maxNumber) maxNumber = num;
          }
        });
        this.usedHandles = numbers;
        this.baseNumber = maxNumber;
      } catch (e) {
        console.error('Fehler beim Laden der Handles!', e);
        this.usedHandles = new Set();
        this.baseNumber = 1000;
      }
    },

    triggerFileDialog() { this.$refs.fileInput.click(); },

    onFileSelected(e) {
      const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
      if (!file) return;
      this.section.imageFile = file;
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result;
        this.checkTransparency(this.imagePreview);
      };
      reader.readAsDataURL(file);
    },

    // Skaliert + komprimiert ein Bild clientseitig (max. 1600px, JPEG ≤ ~900 KB).
    // Verhindert HTTP 413 vom Produktiv-Proxy bei großen Originalbildern.
    async compressImage(file) {
      const MAX_EDGE = 1600;
      const TARGET_BYTES = 900 * 1024;
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        const img = await new Promise((resolve, reject) => {
          const i = new Image();
          i.onload = () => resolve(i);
          i.onerror = reject;
          i.src = dataUrl;
        });

        const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);

        const encode = (q) => new Promise((resolve) => {
          canvas.toBlob((b) => resolve(b), 'image/jpeg', q);
        });

        let quality = 0.85;
        let blob = await encode(quality);
        while (blob && blob.size > TARGET_BYTES && quality > 0.4) {
          quality -= 0.15;
          const next = await encode(quality);
          if (!next) break;
          blob = next;
        }
        return blob || file;
      } catch (e) {
        console.warn('Bild-Komprimierung fehlgeschlagen, nutze Original:', e);
        return file;
      }
    },

    checkTransparency(dataUrl) {
      const img = new Image();
      img.src = dataUrl;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, img.width, img.height).data;
        let hasTransparency = false;
        for (let i = 3; i < imageData.length; i += 4) {
          if (imageData[i] < 255) { hasTransparency = true; break; }
        }
        this.imageHasTransparency = hasTransparency;
      };
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 26px; font-weight: 800; color: #0a1628; letter-spacing: -0.3px; margin-bottom: 4px; }
.page-subtitle { font-size: 13px; color: #888; }

.secondary-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 18px; background: white; color: #0a1628;
  border: 1px solid #eceef2; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.secondary-btn:hover { border-color: #0a1628; }

.grid-split { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: 20px; align-items: start; }

.card-modern { background: white; border: 1px solid #eceef2; border-radius: 16px; padding: 28px; }
.section-title {
  font-size: 16px; font-weight: 700; color: #0a1628;
  display: flex; align-items: center; gap: 10px;
}
.title-icon {
  width: 30px; height: 30px; border-radius: 8px;
  background: rgba(10,22,40,0.06);
  display: flex; align-items: center; justify-content: center;
}

.fg { display: flex; flex-direction: column; position: relative; }
.fl { font-size: 11px; font-weight: 600; color: #999; margin-bottom: 6px; letter-spacing: 0.3px; }
.fl-hint { color: #bbb; font-weight: 400; text-transform: none; letter-spacing: 0; margin-left: 4px; }

.iw {
  display: flex; align-items: center; gap: 10px;
  height: 42px; padding: 0 14px; width: 100%;
  background: #f8f9fb; border: 1px solid #eceef2; border-radius: 10px;
  transition: all 0.15s;
}
.iw.iw-f { border-color: #0a1628; background: white; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.iw-trigger { cursor: pointer; text-align: left; font-family: inherit; background: white !important; }
.iw-trigger:hover:not(.iw-f) { border-color: #c5cbd3; }
.iw-readonly { opacity: 0.7; }
.ii {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 14px; color: #0a1628; font-weight: 500; min-width: 0;
  font-family: inherit; height: 40px;
}
.ii::placeholder { color: #bbb; font-weight: 400; }
.ii[readonly] { opacity: 0.7; cursor: default; }
.ii-val { flex: 1; font-size: 14px; color: #0a1628; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.chev-open { transform: rotate(180deg); transition: transform 0.2s; }

.combo-menu {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  box-shadow: 0 12px 32px rgba(10,22,40,0.12);
  z-index: 50; max-height: 260px; overflow-y: auto;
  padding: 6px; animation: comboFade 0.12s ease;
}
@keyframes comboFade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
.combo-option {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 9px 10px; background: transparent; border: none;
  border-radius: 7px; cursor: pointer; transition: background 0.1s;
  text-align: left; font-family: inherit;
}
.combo-option:hover { background: #f8f9fb; }
.combo-label { flex: 1; font-size: 14px; color: #0a1628; font-weight: 500; }
.combo-divider { height: 1px; background: #eceef2; margin: 4px 0; }

/* Radio / Status Cards */
.radio-option, .status-option {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px; background: white; border: 1px solid #eceef2;
  border-radius: 12px; cursor: pointer; transition: all 0.15s;
}
.radio-option:hover, .status-option:hover { border-color: #c5cbd3; }
.radio-active, .status-active {
  border-color: #0a1628; background: #fafbfc;
  box-shadow: 0 0 0 3px rgba(10,22,40,0.04);
}

.radio-dot, .status-dot {
  width: 20px; height: 20px; min-width: 20px; border-radius: 50%;
  border: 2px solid #c5cbd3; background: white;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.radio-dot-on, .status-dot-on { background: #0a1628; border-color: #0a1628; }
.radio-dot-on { background: radial-gradient(circle, #0a1628 0, #0a1628 5px, transparent 5px); }

.radio-icon, .status-icon {
  width: 32px; height: 32px; min-width: 32px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
}
.radio-body, .status-body { flex: 1; min-width: 0; }
.radio-label, .status-label { font-size: 13px; font-weight: 700; color: #0a1628; }
.radio-desc, .status-desc { font-size: 11px; color: #888; margin-top: 1px; }

.divider-soft { height: 1px; background: #eceef2; }

.info-banner {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 10px 12px; background: rgba(91,91,214,0.08); color: #3A3AC0;
  border-radius: 10px; font-size: 12px; font-weight: 500; line-height: 1.5;
}
/* Icon darf nicht schrumpfen; Text fließt als eigener Block (eigenes <span>),
   sonst werden die einzelnen Text-Segmente zu separaten Flex-Items. */
.info-banner-icon { flex-shrink: 0; margin-top: 1px; }
.info-banner span { flex: 1; min-width: 0; }

.submit-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 14px 20px;
  background: #2E7D32; color: white; border: none; border-radius: 12px;
  font-size: 14px; font-weight: 700; cursor: pointer;
  transition: background 0.15s; font-family: inherit;
}
.submit-btn:hover:not(:disabled) { background: #1B5E20; }
.submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.submit-btn-dark { background: #0a1628; }
.submit-btn-dark:hover:not(:disabled) { background: #1d3457; }

.alert-error {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: rgba(211,47,47,0.08); color: #D32F2F;
  border-radius: 10px; font-size: 13px; font-weight: 500;
}

/* Image side */
.image-side { position: sticky; top: 24px; }
.upload-box {
  position: relative; background: #f3f4f6; border: 1px dashed #c5cbd3;
  border-radius: 12px; overflow: hidden; cursor: pointer; transition: all 0.15s;
  padding: 8px;
}
.upload-box:hover { border-color: #0a1628; }
.upload-box :deep(.v-img) { border-radius: 8px; overflow: hidden; }
.upload-placeholder {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 280px; padding: 24px; text-align: center;
  background: white; border-radius: 8px;
}
.upload-title { font-size: 14px; font-weight: 700; color: #0a1628; margin-bottom: 2px; }
.upload-sub { font-size: 12px; color: #888; }

.bs {
  display: inline-flex; align-items: center; gap: 8px; width: 100%; justify-content: center;
  padding: 10px 14px; border: 1px dashed #c5cbd3; border-radius: 10px; background: #fafbfc;
  font-size: 13px; font-weight: 600; color: #555; cursor: pointer; transition: all 0.15s;
  font-family: inherit;
}
.bs:hover { border-color: #0a1628; color: #0a1628; background: white; }

@media (max-width: 1100px) {
  .grid-split { grid-template-columns: 1fr; }
  .image-side { position: static; }
}
</style>
